import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, message, Modal, Form, Input, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { userAPI, authAPI } from '../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  // 获取用户列表和角色列表
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('获取用户列表错误:', error);
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await userAPI.getRoles();
      setRoles(response.data);
    } catch (error) {
      console.error('获取角色列表错误:', error);
      message.error('获取角色列表失败');
    }
  };

  // 处理添加用户
  const handleAddUser = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 处理编辑用户
  const handleEditUser = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      phone: user.phone,
      open_id: user.open_id,
      role_id: user.role_id
    });
    setIsModalVisible(true);
  };

  // 处理删除用户
  const handleDeleteUser = async (userId, username) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除用户 "${username}" 吗？此操作不可撤销。`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await userAPI.deleteUser(userId);
          message.success('用户删除成功');
          fetchUsers();
        } catch (error) {
          console.error('删除用户错误:', error);
          message.error('删除用户失败');
        }
      }
    });
  };

  // 处理表单提交
  const handleSubmit = async (values) => {
    try {
      if (editingUser) {
        // 更新用户
        await userAPI.updateUser(editingUser.id, values);
        message.success('用户更新成功');
      } else {
        // 创建用户
        await authAPI.register(values);
        message.success('用户创建成功');
      }
      setIsModalVisible(false);
      fetchUsers();
    } catch (error) {
      console.error('保存用户错误:', error);
      message.error('保存用户失败');
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username'
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: '飞书OpenID',
      dataIndex: 'open_id',
      key: 'open_id',
      render: (openId) => openId || '-'
    },
    {
      title: '角色',
      dataIndex: 'role_id',
      key: 'role_id',
      render: (roleId) => {
        const role = roles.find(r => r.id === roleId);
        return role ? role.name : '未知';
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEditUser(record)}
            style={{ marginRight: 8 }}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteUser(record.id, record.username)}
          >
            删除
          </Button>
        </div>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="glass-page-title">用户管理</h1>
      </div>

      <Card className="glass-card" title="用户列表" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUser}>
            添加用户
          </Button>
        </div>
        <div className="glass-table">
          <Table 
            columns={columns} 
            dataSource={users} 
            loading={loading}
            rowKey="id"
            pagination={false}
          />
        </div>
        <div style={{ marginTop: 16, textAlign: 'left', fontSize: '14px', color: 'rgba(255, 255, 255, 0.5)' }}>
          共 {users.length} 条记录
        </div>
      </Card>

      {/* 添加/编辑用户模态框 */}
      <Modal
        className="glass-modal"
        title={editingUser ? '编辑用户' : '添加用户'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          className="glass-form"
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input prefix={<UserOutlined />} />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="手机号"
            rules={[{ required: true, message: '请输入手机号' }, { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的中国手机号' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="open_id"
            label="飞书OpenID"
            rules={[{ max: 50, message: '飞书OpenID长度不能超过50个字符' }]}
          >
            <Input placeholder="请输入飞书OpenID（可选）" />
          </Form.Item>
          
          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码长度至少为6位' }]}
            >
              <Input.Password />
            </Form.Item>
          )}
          
          <Form.Item
            name="role_id"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              {roles.map(role => (
                <Select.Option key={role.id} value={role.id}>
                  {role.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item style={{ textAlign: 'right' }}>
            <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
